'use client';

import { ChevronDown, X } from 'lucide-react';
import * as React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

import { cn } from '@evolve/common-ui';
import { TSelectOption } from '../select';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface CustomSelectProps<T> {
  options: TSelectOption<T>[];
  value?: TSelectOption<T>;
  onChange?: (value: TSelectOption<T> | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  isClearable?: boolean;
  className?: string;
  name?: string;
  renderOption?: (option: TSelectOption<T>) => React.ReactNode;
  renderValue?: (option: TSelectOption<T>) => React.ReactNode;
  onInputChange?: (value: string) => void;
  shouldFilter?: boolean;
}

const CustomSelect = React.forwardRef<HTMLDivElement, CustomSelectProps<any>>(
  (
    {
      placeholder = 'Selecionar...',
      emptyMessage = 'Sem opções...',
      isClearable = false,
      shouldFilter = false,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const listId = React.useId();
    const inputRef = React.useRef<HTMLInputElement>(null);

    const focusInput = React.useCallback(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }, []);

    const openAndFocusInput = React.useCallback(() => {
      if (props.disabled) {
        return;
      }
      setOpen(true);
      focusInput();
    }, [focusInput, props.disabled]);

    const handleOpenChange = React.useCallback(
      (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) {
          focusInput();
        }
      },
      [focusInput],
    );

    const handleSelect = (currentValue: TSelectOption<any>) => {
      props.onChange?.(currentValue);
      setOpen(false);
      setSearchValue('');
    };

    return (
      <Command
        shouldFilter={shouldFilter}
        className="overflow-visible bg-transparent"
      >
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div
              ref={ref}
              role="combobox"
              aria-controls={listId}
              aria-expanded={open}
              aria-disabled={props.disabled}
              tabIndex={props.disabled ? -1 : 0}
              onClick={openAndFocusInput}
              onBlur={props.onBlur}
              className={cn(
                'border-evolve-primary bg-background ring-offset-background flex w-full items-center justify-between border-2 p-2 text-sm transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'cursor-text',
                !props.value && 'text-muted-foreground',
                props.disabled && 'cursor-not-allowed opacity-50',
                props.className,
              )}
            >
              {(!props.value || open) && (
                <CommandInput
                  ref={inputRef}
                  autoFocus={open}
                  value={searchValue}
                  disabled={props.disabled}
                  onFocus={() => setOpen(true)}
                  onBlur={props.onBlur}
                  onClick={(event) => event.stopPropagation()}
                  onValueChange={(value) => {
                    if (!open) {
                      setOpen(true);
                    }
                    setSearchValue(value);
                    props.onInputChange?.(value);
                  }}
                  placeholder={placeholder}
                />
              )}

              {props.value && !open && (
                <span className="text-foreground w-full truncate text-left">
                  {props.value
                    ? props.renderValue
                      ? props.renderValue(props.value)
                      : props.value.label
                    : placeholder}
                </span>
              )}
              {isClearable && props.value && !props.disabled && !open && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    props.onChange?.(null);
                    setSearchValue('');
                  }}
                  className="text-evolve-secondary-700 hover:text-evolve-secondary-900 rounded p-0.5 focus:outline-none"
                  aria-label="Limpar seleção"
                >
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </button>
              )}
              <div className="items-centesr border-1 border-evolve-primary ml-1 flex h-full gap-0.5 border-l pl-2">
                <ChevronDown
                  className="text-evolve-primary h-5 w-5 shrink-0"
                  strokeWidth={2.52}
                />
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="border-evolve-primary w-[var(--radix-popover-trigger-width)] rounded-none border-2 bg-white p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <CommandList id={listId} className="max-h-[250px]">
              <CommandEmpty className="text-evolve-secondary-700 p-2 text-center">
                {emptyMessage}
              </CommandEmpty>
              <CommandGroup>
                {props.options.map((option) => (
                  <CommandItem
                    key={option.label}
                    value={option.label}
                    onSelect={() => handleSelect(option)}
                    className={cn(
                      'text-evolve-highlight cursor-pointer truncate px-4 py-3 text-sm font-medium',
                      'data-[selected=true]:bg-evolve-highlight-50/20',
                      'hover:!bg-evolve-highlight-50/50',
                    )}
                  >
                    {props.renderOption
                      ? props.renderOption(option)
                      : option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    );
  },
);

CustomSelect.displayName = 'CustomSelect';

export { CustomSelect };
